import React, { useEffect, useState, useMemo } from 'react';
import {
  ClipboardList, Mail, Phone, Calendar, ChevronDown, ChevronUp, Trash2, X,
  CircleDot, CreditCard, Search as SearchIcon, Award, RotateCcw,
  Link2, Copy, Plus, Send, ExternalLink, Zap, FileDown, CheckCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pipeline stages with admin-friendly labels + colour treatment.
// Keep keys in sync with BESPOKE_STAGES on the backend.
const STAGES = [
  { id: 'new',              label: 'New Brief',        color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { id: 'contacted',        label: 'Contacted',        color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  { id: 'brief_confirmed',  label: 'Brief Confirmed',  color: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  { id: 'deposit_paid',     label: 'Deposit Paid',     color: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40' },
  { id: 'sourcing',         label: 'Sourcing',         color: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  { id: 'presented',        label: 'Deal Presented',   color: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  { id: 'won',              label: 'Won',              color: 'bg-green-500/15 text-green-300 border-green-500/30' },
  { id: 'lost',             label: 'Lost',             color: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
  { id: 'refunded',         label: 'Refunded',         color: 'bg-red-500/15 text-red-300 border-red-500/30' },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map(s => [s.id, s.label]));
const STAGE_COLOR = Object.fromEntries(STAGES.map(s => [s.id, s.color]));

const DEPOSIT_STATUSES = [
  { id: 'pending',  label: 'Pending',  color: 'text-yellow-300' },
  { id: 'paid',     label: 'Paid',     color: 'text-[#D4AF37]' },
  { id: 'refunded', label: 'Refunded', color: 'text-red-300' },
];

// Format ISO datetime → "3d ago" / "12h ago" / "5m ago"
const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

// Returns { label, color, expired } for the 6-week sourcing countdown.
const countdownFor = (depositPaidAt, weeks = 6) => {
  if (!depositPaidAt) return null;
  const start = new Date(depositPaidAt).getTime();
  if (Number.isNaN(start)) return null;
  const deadline = start + weeks * 7 * 24 * 60 * 60 * 1000;
  const diff = deadline - Date.now();
  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: 'text-red-300', expired: true };
  if (days === 0) return { label: 'Due today', color: 'text-orange-300', expired: false };
  if (days <= 7) return { label: `${days}d left`, color: 'text-orange-300', expired: false };
  return { label: `${days}d left`, color: 'text-white/60', expired: false };
};

const Pill = ({ children, color, onClick, testid }) => (
  <span
    onClick={onClick}
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${color} ${onClick ? 'cursor-pointer hover:opacity-90' : ''}`}
    data-testid={testid}
  >{children}</span>
);

const BriefRow = ({ brief, onUpdate, onDelete, depositWeeks }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(brief.notes || '');

  const cd = countdownFor(brief.deposit_paid_at, depositWeeks);
  const stage = brief.status || 'new';
  const deposit = brief.deposit_status || 'pending';

  const patch = async (body) => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests/${brief.id}?password=${encodeURIComponent(window.__bspw || '')}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        onUpdate(d.request);
        toast.success('Updated');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Update failed');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const summary = useMemo(() => {
    const bits = [];
    if (brief.preferred_countries?.length) bits.push(brief.preferred_countries.join(', '));
    if (brief.preferred_cities) bits.push(brief.preferred_cities);
    if (brief.asset_classes?.length) bits.push(brief.asset_classes.join(', '));
    if (brief.investment_budget) bits.push(brief.investment_budget);
    return bits.join(' · ');
  }, [brief]);

  return (
    <div className={`bg-white/[0.03] border ${open ? 'border-[#D4AF37]/30' : 'border-white/10'} rounded-lg overflow-hidden transition-colors`} data-testid={`brief-row-${brief.id}`}>
      {/* Row header */}
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white text-sm font-semibold truncate" data-testid={`brief-name-${brief.id}`}>{brief.full_name}</p>
            <span className="text-white/30 text-[11px]">·</span>
            <span className="text-white/40 text-[11px]">{timeAgo(brief.created_at)}</span>
          </div>
          <p className="text-white/45 text-[11px] truncate">{brief.email}{brief.phone ? ` · ${brief.phone}` : ''}{summary ? ` · ${summary}` : ''}</p>
        </div>

        <Pill color={STAGE_COLOR[stage] || STAGE_COLOR.new} testid={`brief-stage-${brief.id}`}>{STAGE_LABEL[stage] || stage}</Pill>

        {/* Signature pill */}
        {brief.signature?.signed_at && (
          <span
            className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded flex items-center gap-1"
            title={`Signed by ${brief.signature.typed_name || '—'} on ${new Date(brief.signature.signed_at).toLocaleString()}`}
            data-testid={`brief-signed-pill-${brief.id}`}
          >
            <CheckCircle className="w-3 h-3" /> Signed
          </span>
        )}

        {/* Deposit */}
        <div className="flex items-center gap-1.5 text-[11px]">
          <CreditCard className="w-3.5 h-3.5 text-white/40" />
          <span className={DEPOSIT_STATUSES.find(d => d.id === deposit)?.color || 'text-white/60'}>
            {DEPOSIT_STATUSES.find(d => d.id === deposit)?.label || deposit}
          </span>
        </div>

        {/* 6-week countdown */}
        {cd && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <Calendar className={`w-3.5 h-3.5 ${cd.color}`} />
            <span className={cd.color} data-testid={`brief-countdown-${brief.id}`}>{cd.label}</span>
          </div>
        )}

        <button onClick={() => setOpen(o => !o)} className="text-white/40 hover:text-white p-1.5 rounded" title={open ? 'Collapse' : 'Expand'} data-testid={`brief-toggle-${brief.id}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded panel */}
      {open && (
        <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
          {/* Stage selector */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5">Pipeline Stage</p>
              <select
                value={stage}
                onChange={e => patch({ status: e.target.value })}
                disabled={saving}
                className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm cursor-pointer focus:outline-none"
                data-testid={`brief-stage-select-${brief.id}`}
              >
                {STAGES.map(s => <option key={s.id} value={s.id} className="bg-[#0a1a14]">{s.label}</option>)}
              </select>
            </div>

            <div>
              <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5">Deposit Status</p>
              <select
                value={deposit}
                onChange={e => patch({ deposit_status: e.target.value })}
                disabled={saving}
                className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm cursor-pointer focus:outline-none"
                data-testid={`brief-deposit-select-${brief.id}`}
              >
                {DEPOSIT_STATUSES.map(s => <option key={s.id} value={s.id} className="bg-[#0a1a14]">{s.label}</option>)}
              </select>
            </div>

            {brief.deposit_paid_at && (
              <div>
                <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5">Deposit Paid</p>
                <p className="text-white/85 text-sm py-2">{new Date(brief.deposit_paid_at).toLocaleDateString()} <span className="text-white/40 text-[11px]">({timeAgo(brief.deposit_paid_at)})</span></p>
              </div>
            )}
          </div>

          {/* Full brief */}
          <div>
            <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-2">Brief Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] text-white/75 bg-[#0a1a14]/40 rounded-md p-4">
              {brief.country_residence && <p><span className="text-white/40">Country of residence:</span> {brief.country_residence}</p>}
              {brief.company && <p><span className="text-white/40">Company:</span> {brief.company}</p>}
              {brief.preferred_countries?.length > 0 && <p><span className="text-white/40">Countries:</span> {brief.preferred_countries.join(', ')}</p>}
              {brief.preferred_cities && <p><span className="text-white/40">Cities:</span> {brief.preferred_cities}</p>}
              {brief.asset_classes?.length > 0 && <p><span className="text-white/40">Asset classes:</span> {brief.asset_classes.join(', ')}</p>}
              {brief.strategies?.length > 0 && <p><span className="text-white/40">Strategies:</span> {brief.strategies.join(', ')}{brief.other_strategy ? ` · ${brief.other_strategy}` : ''}</p>}
              {brief.investment_budget && <p><span className="text-white/40">Budget:</span> {brief.investment_budget}</p>}
              {brief.target_yield && <p><span className="text-white/40">Yield target:</span> {brief.target_yield}</p>}
              {brief.capital_growth && <p><span className="text-white/40">Growth target:</span> {brief.capital_growth}</p>}
              {brief.holding_period && <p><span className="text-white/40">Hold period:</span> {brief.holding_period}</p>}
              {brief.timeline && <p><span className="text-white/40">Timeline:</span> {brief.timeline}</p>}
              {brief.funding_position?.length > 0 && <p><span className="text-white/40">Funding:</span> {brief.funding_position.join(', ')}</p>}
              {brief.deposit_available && <p><span className="text-white/40">Deposit available:</span> {brief.deposit_available}</p>}
              {brief.experience_level && <p><span className="text-white/40">Experience:</span> {brief.experience_level}</p>}
              {brief.risk_appetite && <p><span className="text-white/40">Risk:</span> {brief.risk_appetite}</p>}
              {brief.amenities && <p className="md:col-span-2"><span className="text-white/40">Amenities:</span> {brief.amenities}</p>}
              {brief.requirements_exclusions && <p className="md:col-span-2"><span className="text-white/40">Requirements/Exclusions:</span> {brief.requirements_exclusions}</p>}
              {brief.additional_notes && <p className="md:col-span-2"><span className="text-white/40">Investor notes:</span> {brief.additional_notes}</p>}
            </div>
          </div>

          {/* Internal notes */}
          <div>
            <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5">Internal Notes</p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => { if (notes !== (brief.notes || '')) patch({ notes }); }}
              rows={3}
              placeholder="Sourcer notes, contract status, last call, next action…"
              className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none resize-none"
              data-testid={`brief-notes-${brief.id}`}
            />
            <p className="text-white/30 text-[10px] mt-1">Notes auto-save on blur.</p>
          </div>

          {/* Investor portal */}
          <InvestorPortalBlock brief={brief} onChange={onUpdate} />

          {/* Curated deals */}
          <CuratedDealsBlock brief={brief} onChange={onUpdate} />

          {/* Investor updates */}
          <InvestorUpdatesBlock brief={brief} onChange={onUpdate} />

          {/* Test Notification — fire a dry-run push without changing the brief */}
          <TestNotifyBlock brief={brief} />

          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div className="flex items-center gap-3 text-[11px]">
              <a
                href={`mailto:${brief.email}`}
                className="text-white/50 hover:text-[#D4AF37] inline-flex items-center gap-1"
                data-testid={`brief-email-${brief.id}`}
              ><Mail className="w-3.5 h-3.5" /> Email</a>
              {brief.phone && (
                <a
                  href={`https://wa.me/${brief.phone.replace(/[^\d]/g, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-white/50 hover:text-[#25D366] inline-flex items-center gap-1"
                  data-testid={`brief-wa-${brief.id}`}
                ><Phone className="w-3.5 h-3.5" /> WhatsApp</a>
              )}
              <a
                href={`/admin/crm`}
                className="text-white/50 hover:text-[#D4AF37] inline-flex items-center gap-1"
              >Open CRM →</a>
            </div>
            <button
              onClick={() => onDelete(brief)}
              className="text-white/30 hover:text-red-400 inline-flex items-center gap-1 text-[11px]"
              data-testid={`brief-delete-${brief.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete brief
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Sub-blocks rendered inside BriefRow ==============

const InvestorPortalBlock = ({ brief, onChange }) => {
  const url = brief.access_token ? `${window.location.origin}/my-brief?token=${brief.access_token}` : '';
  const [regenerating, setRegenerating] = useState(false);
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success('Investor link copied'); }
    catch { toast.info(url, { duration: 8000 }); }
  };
  const reissue = async () => {
    const msg = brief.email
      ? `Re-issue this investor's access link?\n\nThe previous link will stop working immediately. A fresh link will be EMAILED to ${brief.email}.`
      : `Re-issue this investor's access link?\n\nThe previous link will stop working immediately.\n\n(No email on file - no notification will be sent.)`;
    if (!window.confirm(msg)) return;
    setRegenerating(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/bespoke-requests/${brief.id}/regenerate-token?password=${encodeURIComponent(window.__bspw || '')}&send_email=${brief.email ? 'true' : 'false'}`,
        { method: 'POST' },
      );
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(d.email_sent ? `New link issued + emailed to ${brief.email}` : 'New link issued');
        onChange({ ...brief, access_token: d.access_token });
      } else {
        toast.error(d.detail || 'Could not re-issue link');
      }
    } catch { toast.error('Network error'); }
    setRegenerating(false);
  };
  if (!url) return null;
  return (
    <div className="bg-[#D4AF37]/[0.04] border border-[#D4AF37]/20 rounded-lg p-4" data-testid={`investor-portal-${brief.id}`}>
      <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" /> Investor Portal Link
      </p>
      <p className="text-white/45 text-[11px] mb-3">Already sent in the auto-reply email. Use the buttons below to resend, open, or revoke.</p>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="flex-1 min-w-[200px] bg-[#0a1a14] text-white/80 text-[11px] px-3 py-2 rounded border border-white/10 truncate font-mono">
          {url}
        </code>
        <button onClick={copy} className="px-3 py-2 rounded border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs flex items-center gap-1.5" data-testid={`investor-portal-copy-${brief.id}`}>
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
        <a href={url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded border border-white/15 text-white/70 hover:text-white text-xs flex items-center gap-1.5" data-testid={`investor-portal-open-${brief.id}`}>
          <ExternalLink className="w-3.5 h-3.5" /> Open
        </a>
        <button
          onClick={reissue}
          disabled={regenerating}
          className="px-3 py-2 rounded border border-red-400/30 text-red-300 hover:bg-red-500/10 text-xs flex items-center gap-1.5 disabled:opacity-40"
          data-testid={`investor-portal-reissue-${brief.id}`}
          title="Invalidate the current link and issue a fresh one"
        >
          <RotateCcw className="w-3.5 h-3.5" /> {regenerating ? 'Re-issuing…' : 'Re-issue'}
        </button>
      </div>
    </div>
  );
};

const CuratedDealsBlock = ({ brief, onChange }) => {
  const [slugs, setSlugs] = useState((brief.curated_deals || []).join(', '));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const arr = slugs.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests/${brief.id}/curated-deals?password=${encodeURIComponent(window.__bspw || '')}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ curated_deals: arr }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Saved ${d.curated_deals.length} curated deal slug${d.curated_deals.length !== 1 ? 's' : ''}`);
        onChange({ ...brief, curated_deals: d.curated_deals });
      } else toast.error('Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  return (
    <div data-testid={`curated-deals-${brief.id}`}>
      <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5 flex items-center gap-1.5">
        <Award className="w-3.5 h-3.5 text-[#D4AF37]" /> Curated Deal Slugs <span className="text-white/30 normal-case tracking-normal">(shown on investor portal)</span>
      </p>
      <textarea
        value={slugs}
        onChange={e => setSlugs(e.target.value)}
        rows={2}
        placeholder="comma-separated deal slugs, e.g. prime-east-legon-luxury-development, accra-townhomes…"
        className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none resize-none font-mono"
        data-testid={`curated-deals-input-${brief.id}`}
      />
      <div className="flex items-center justify-between mt-1.5">
        <p className="text-white/30 text-[10px]">Get slugs from Admin → Deal Sourcing. Order matters: first in list shows first on portal.</p>
        <Button onClick={save} disabled={saving} size="sm" className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-7 px-3 text-xs" data-testid={`curated-deals-save-${brief.id}`}>
          {saving ? 'Saving…' : 'Save deals'}
        </Button>
      </div>
    </div>
  );
};

const InvestorUpdatesBlock = ({ brief, onChange }) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const updates = brief.investor_updates || [];

  const send = async () => {
    if (!draft.trim()) { toast.error('Write a message first'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests/${brief.id}/updates?password=${encodeURIComponent(window.__bspw || '')}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: draft.trim() }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success('Update posted to investor portal');
        onChange({ ...brief, investor_updates: [...updates, d.update] });
        setDraft('');
      } else toast.error('Post failed');
    } catch { toast.error('Network error'); }
    setSending(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this update from the investor portal?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests/${brief.id}/updates/${id}?password=${encodeURIComponent(window.__bspw || '')}`, { method: 'DELETE' });
      if (r.ok) {
        onChange({ ...brief, investor_updates: updates.filter(u => u.id !== id) });
        toast.success('Update removed');
      } else toast.error('Delete failed');
    } catch { toast.error('Network error'); }
  };

  return (
    <div data-testid={`updates-${brief.id}`}>
      <p className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5 flex items-center gap-1.5">
        <Send className="w-3.5 h-3.5 text-[#D4AF37]" /> Updates for Investor <span className="text-white/30 normal-case tracking-normal">(visible on portal timeline)</span>
      </p>
      {updates.length > 0 && (
        <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
          {[...updates].reverse().map(u => (
            <div key={u.id} className="flex items-start gap-2 bg-[#0a1a14]/50 border border-white/5 rounded p-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-white/85 text-[12px] leading-snug">{u.message}</p>
                <p className="text-white/30 text-[10px] mt-1">{new Date(u.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => remove(u.id)} className="text-white/30 hover:text-red-400 p-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={2}
          placeholder="Post an update visible to the investor (e.g. &quot;Just toured 2 properties in East Legon — full reports coming Friday.&quot;)"
          className="flex-1 bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none resize-none"
          data-testid={`updates-input-${brief.id}`}
        />
        <Button onClick={send} disabled={sending || !draft.trim()} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid={`updates-send-${brief.id}`}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};


// ============== Test Notify — fire a dry-run push without changing the brief ==============
const TEST_STAGES = [
  { id: 'brief_confirmed', label: 'Brief Confirmed' },
  { id: 'deposit_paid',    label: 'Deposit Paid' },
  { id: 'sourcing',        label: 'Sourcing Live' },
  { id: 'presented',       label: 'Opportunities Presented' },
  { id: 'won',             label: 'Won' },
];

const TestNotifyBlock = ({ brief }) => {
  const [stage, setStage] = useState('brief_confirmed');
  const [channels, setChannels] = useState({ admin: true, investor: true });
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState(null);

  const fire = async () => {
    const picked = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (picked.length === 0) {
      toast.error('Pick at least one audience (Admin or Investor).');
      return;
    }
    setSending(true);
    setLast(null);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/bespoke-requests/${brief.id}/test-notify?password=${encodeURIComponent(window.__bspw || '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage, channels: picked }),
        }
      );
      const data = await r.json();
      if (r.ok && data.success) {
        setLast(data);
        const parts = [];
        if (data.results?.admin?.ok) parts.push('Admin push fired');
        if (data.results?.investor?.sent != null) parts.push(`Investor: ${data.results.investor.sent} channel(s) delivered`);
        toast.success(parts.join(' · ') || 'Test fired');
      } else {
        toast.error(data.detail || 'Test failed');
      }
    } catch { toast.error('Network error'); }
    setSending(false);
  };

  const downloadJourneyDeck = () => {
    const url = `${API_URL}/api/admin/bespoke-requests/${brief.id}/journey-deck.pdf?password=${encodeURIComponent(window.__bspw || '')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Journey deck downloading…');
  };

  const [emailing, setEmailing] = useState(false);
  const emailJourneyDeck = async () => {
    if (!brief.email) {
      toast.error('This brief has no email on file');
      return;
    }
    if (!window.confirm(`Email the personalised journey deck PDF to ${brief.email}?`)) return;
    setEmailing(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/bespoke-requests/${brief.id}/email-journey-deck?password=${encodeURIComponent(window.__bspw || '')}`,
        { method: 'POST' },
      );
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Sent to ${d.to}`);
      } else {
        toast.error(d.detail || 'Send failed');
      }
    } catch { toast.error('Network error'); }
    setEmailing(false);
  };

  // Re-fetch preview when stage changes (without re-sending notifications)
  useEffect(() => {
    if (!last) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/admin/bespoke-requests/${brief.id}/test-notify?password=${encodeURIComponent(window.__bspw || '')}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stage, channels: [] }), // empty channels = preview-only
          }
        );
        const data = await r.json();
        if (!cancelled && r.ok && data.success) {
          setLast(prev => ({ ...prev, investor_preview: data.investor_preview, results: prev?.results || {} }));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  return (
    <div
      className="bg-[#0a1a14] border border-[#D4AF37]/15 rounded-lg p-4 space-y-3"
      data-testid={`test-notify-${brief.id}`}
    >
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-white text-xs font-semibold uppercase tracking-widest">Dry-Run Notification</p>
        <span className="text-white/30 text-[10px]">Fire a synthetic stage push without changing the brief</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2.5 items-center">
        <select
          value={stage}
          onChange={e => setStage(e.target.value)}
          className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
          data-testid={`test-notify-stage-${brief.id}`}
        >
          {TEST_STAGES.map(s => <option key={s.id} value={s.id}>{`Simulate → ${s.label}`}</option>)}
        </select>
        <div className="flex items-center gap-3 text-[11px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={channels.admin}
              onChange={e => setChannels(c => ({ ...c, admin: e.target.checked }))}
              className="accent-[#D4AF37] w-3.5 h-3.5"
              data-testid={`test-notify-admin-toggle-${brief.id}`}
            />
            <span className="text-white/70">Admin team</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={channels.investor}
              onChange={e => setChannels(c => ({ ...c, investor: e.target.checked }))}
              className="accent-[#25D366] w-3.5 h-3.5"
              data-testid={`test-notify-investor-toggle-${brief.id}`}
            />
            <span className="text-white/70">Investor</span>
          </label>
        </div>
        <Button
          onClick={fire}
          disabled={sending}
          size="sm"
          className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9 px-4 text-xs font-semibold"
          data-testid={`test-notify-fire-${brief.id}`}
        >
          <Zap className="w-3.5 h-3.5 mr-1.5" />
          {sending ? 'Firing…' : 'Send Test Now'}
        </Button>
      </div>

      {/* Journey-deck export — sleek leave-behind PDF for HNW pitches */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5">
        <p className="text-white/45 text-[10px] leading-tight">
          <span className="text-[#D4AF37] font-semibold">Pitch asset:</span> 7-page personalised PDF walking this prospect through every stage. Edit the wording globally in Admin → <strong>Journey Deck</strong>.
        </p>
        <div className="flex gap-1.5 shrink-0">
          <Button
            onClick={emailJourneyDeck}
            size="sm"
            variant="outline"
            disabled={emailing || !brief.email}
            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-3 text-xs whitespace-nowrap disabled:opacity-40"
            data-testid={`email-journey-deck-${brief.id}`}
            title={brief.email ? `Email PDF to ${brief.email}` : 'No email on file'}
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" /> {emailing ? 'Sending…' : 'Email to Investor'}
          </Button>
          <Button
            onClick={downloadJourneyDeck}
            size="sm"
            variant="outline"
            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-3 text-xs whitespace-nowrap"
            data-testid={`journey-deck-pdf-${brief.id}`}
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>
      {last?.results && (
        <div className="flex flex-wrap gap-1.5 text-[10px] pt-1" data-testid={`test-notify-result-${brief.id}`}>
          {last.results.admin && (
            <span className={`px-2 py-1 rounded border ${last.results.admin.ok ? 'border-green-500/30 bg-green-500/10 text-green-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
              Admin: {last.results.admin.ok ? 'OK' : (last.results.admin.error || 'failed')}
            </span>
          )}
          {last.results.investor && last.results.investor.channels && Object.entries(last.results.investor.channels).map(([ch, ok]) => (
            <span key={ch} className={`px-2 py-1 rounded border ${ok ? 'border-green-500/30 bg-green-500/10 text-green-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
              Investor · {ch}: {ok ? 'OK' : 'failed'}
            </span>
          ))}
          {last.results.investor && last.results.investor.skipped && (
            <span className="px-2 py-1 rounded border border-white/15 bg-white/5 text-white/50">
              Investor: {last.results.investor.skipped}
            </span>
          )}
        </div>
      )}

      {last?.investor_preview && <InvestorPreviewPane preview={last.investor_preview} />}
    </div>
  );
};


// ============== Investor Preview Pane — what the investor SEES at this stage ==============
const InvestorPreviewPane = ({ preview }) => {
  if (!preview) return null;
  const prefs = preview.notify_prefs || {};
  const channelBadge = (label, on) => (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${on ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-white/5 text-white/30 border border-white/10'}`}>
      {label}
    </span>
  );
  return (
    <div className="mt-2 border-t border-[#D4AF37]/15 pt-3 space-y-3" data-testid="investor-preview-pane">
      <div className="flex items-center gap-2">
        <ExternalLink className="w-3.5 h-3.5 text-[#D4AF37]/70" />
        <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">As the investor sees it</p>
        {preview.portal_url && (
          <a
            href={preview.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-[10px] text-[#D4AF37] hover:underline flex items-center gap-1"
            data-testid="investor-preview-portal-link"
          >
            Open live portal <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Two-column polished snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
        {/* LEFT: pipeline + meta */}
        <div className="bg-[#070f0c] border border-white/8 rounded-md p-3 space-y-3">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-semibold mb-1">Pipeline highlight</p>
            <div className="bg-gradient-to-r from-[#D4AF37]/20 to-transparent border-l-2 border-[#D4AF37] px-2.5 py-2 rounded-r">
              <p className="text-white text-sm font-bold">{preview.stage_label}</p>
            </div>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-semibold mb-1">Investor</p>
            <p className="text-white text-sm font-semibold truncate">{preview.investor_name || '—'}</p>
            <p className="text-white/40 text-[11px] truncate">{preview.investor_email || ''}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-semibold mb-1">Opted-in channels</p>
            <div className="flex flex-wrap gap-1">
              {channelBadge('Email',    prefs.email)}
              {channelBadge('WhatsApp', prefs.whatsapp)}
              {channelBadge('Telegram', prefs.telegram && preview.telegram_connected)}
            </div>
            {prefs.telegram && !preview.telegram_connected && (
              <p className="text-orange-300/80 text-[10px] mt-1">⚠ Telegram opted-in but not connected yet</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <p className="text-white/40 uppercase tracking-widest font-semibold">Curated deals</p>
              <p className="text-white font-bold text-sm">{preview.curated_deals_count}</p>
            </div>
            <div>
              <p className="text-white/40 uppercase tracking-widest font-semibold">Timeline updates</p>
              <p className="text-white font-bold text-sm">{preview.updates_count}</p>
            </div>
          </div>
        </div>

        {/* RIGHT: email/notification copy */}
        <div className="bg-[#070f0c] border border-white/8 rounded-md p-4 space-y-2.5">
          <p className="text-[9px] uppercase tracking-widest text-white/40 font-semibold">Outbound message copy</p>
          {preview.email_subject && (
            <div className="text-[11px]">
              <span className="text-white/40">Subject: </span>
              <span className="text-white font-semibold">{preview.email_subject}</span>
            </div>
          )}
          {preview.headline && (
            <h4 className="text-[#D4AF37] text-lg font-bold leading-tight font-serif">
              {preview.headline}
            </h4>
          )}
          {preview.body && (
            <p className="text-white/80 text-xs leading-relaxed whitespace-pre-line">{preview.body}</p>
          )}
          {preview.curated_deals_count > 0 && (
            <div className="pt-2 border-t border-white/8">
              <p className="text-[9px] uppercase tracking-widest text-white/40 font-semibold mb-1.5">Featured in portal</p>
              <div className="space-y-1">
                {preview.curated_deals.slice(0, 3).map(d => (
                  <div key={d.slug} className="text-[11px] text-white/70 truncate">
                    <span className="text-[#D4AF37]">◆</span> {(d.title || d.slug).replace(/\n/g, ' · ').replace(/\|/g, '·')}
                    {d.location && <span className="text-white/40"> — {d.location}</span>}
                  </div>
                ))}
                {preview.curated_deals_count > 3 && (
                  <p className="text-white/30 text-[10px] italic">+ {preview.curated_deals_count - 3} more</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============== Pipeline Health — admin command-centre widget ==============
const HEALTH_CARDS = [
  {
    key: 'deposit_pending',     label: 'Deposits Pending',   icon: CreditCard,
    tint: 'border-yellow-500/40 bg-yellow-500/5 text-yellow-100',
    iconTint: 'text-yellow-300',
    detail: h => h.max_days_waiting > 0 ? `Longest wait: ${h.max_days_waiting}d` : 'No deposits awaited',
  },
  {
    key: 'no_contact_48h',      label: 'No Contact in 48h+', icon: Calendar,
    tint: 'border-red-500/40 bg-red-500/5 text-red-100',
    iconTint: 'text-red-300',
    detail: h => h.briefs?.[0]?.days_since_contact ? `Oldest: ${h.briefs[0].days_since_contact}d` : 'All on top of it',
  },
  {
    key: 'signed_unfunded',     label: 'Signed but Unfunded', icon: CheckCircle,
    tint: 'border-orange-500/40 bg-orange-500/5 text-orange-100',
    iconTint: 'text-orange-300',
    detail: () => 'Awaiting holding deposit',
  },
  {
    key: 'window_expiring_soon', label: '6-Week Window Closing', icon: Zap,
    tint: 'border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37]',
    iconTint: 'text-[#D4AF37]',
    detail: h => h.briefs?.[0]?.days_to_deadline != null ? `Closest: ${h.briefs[0].days_to_deadline}d` : 'None in next 7d',
  },
];

const PipelineHealth = ({ health, active, onPick }) => {
  const activeKey = active.startsWith('h:') ? active.slice(2) : null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-health">
      {HEALTH_CARDS.map(card => {
        const bucket = health[card.key] || { count: 0 };
        const Icon = card.icon;
        const isActive = activeKey === card.key;
        return (
          <button
            key={card.key}
            onClick={() => onPick(isActive ? 'all' : `h:${card.key}`)}
            disabled={bucket.count === 0}
            className={`text-left rounded-xl border p-4 transition-all ${
              isActive
                ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/60 text-white'
                : bucket.count === 0
                  ? 'border-white/10 bg-white/[0.015] text-white/30 cursor-default'
                  : `${card.tint} hover:scale-[1.02] hover:brightness-110 cursor-pointer`
            }`}
            data-testid={`pipeline-health-${card.key}`}
          >
            <div className="flex items-center justify-between">
              <Icon className={`w-4 h-4 ${bucket.count === 0 ? 'text-white/30' : card.iconTint}`} />
              <span className="text-2xl font-bold tabular-nums">{bucket.count}</span>
            </div>
            <p className="text-[10px] uppercase tracking-widest font-semibold mt-2 opacity-80">{card.label}</p>
            <p className="text-[10px] mt-0.5 opacity-50">{card.detail(bucket)}</p>
          </button>
        );
      })}
    </div>
  );
};


// ============== BespokeBriefsTab main ==============
export const BespokeBriefsTab = ({ getStoredPassword }) => {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [depositWeeks, setDepositWeeks] = useState(6);
  const [health, setHealth] = useState(null);
  // 'all' | special filter id keys like 'h:deposit_pending' | 'h:no_contact_48h' | 'h:signed_unfunded' | 'h:window_expiring_soon'

  // Expose the password to BriefRow without prop-drilling on every call.
  useEffect(() => {
    window.__bspw = getStoredPassword();
  }, [getStoredPassword]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests?password=${getStoredPassword()}`);
      if (r.ok) setBriefs(await r.json());
      else toast.error('Could not load briefs');
    } catch { toast.error('Network error'); }
    setLoading(false);
  };

  const loadSettings = async () => {
    try {
      const r = await fetch(`${API_URL}/api/public/bespoke-sourcing-settings`);
      if (r.ok) {
        const d = await r.json();
        setDepositWeeks(d.deposit_period_weeks || 6);
      }
    } catch { /* keep default */ }
  };

  const loadHealth = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-pipeline-health?password=${getStoredPassword()}`);
      if (r.ok) setHealth(await r.json());
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadSettings(); loadHealth(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Health-bucket filters take precedence
    const healthBucket = filter.startsWith('h:') ? filter.slice(2) : null;
    const healthIds = healthBucket && health ? new Set((health[healthBucket]?.briefs || []).map(b => b.id)) : null;
    return briefs.filter(b => {
      if (healthIds && !healthIds.has(b.id)) return false;
      if (!healthIds && filter !== 'all' && (b.status || 'new') !== filter) return false;
      if (!q) return true;
      return (
        (b.full_name || '').toLowerCase().includes(q) ||
        (b.email || '').toLowerCase().includes(q) ||
        (b.phone || '').toLowerCase().includes(q) ||
        (b.preferred_cities || '').toLowerCase().includes(q) ||
        (b.preferred_countries || []).join(' ').toLowerCase().includes(q)
      );
    });
  }, [briefs, filter, search, health]);

  const stageCounts = useMemo(() => {
    const counts = { all: briefs.length };
    STAGES.forEach(s => { counts[s.id] = 0; });
    briefs.forEach(b => { counts[b.status || 'new'] = (counts[b.status || 'new'] || 0) + 1; });
    return counts;
  }, [briefs]);

  const onUpdate = (updated) => setBriefs(prev => prev.map(b => b.id === updated.id ? updated : b));

  const onDelete = async (brief) => {
    if (!window.confirm(`Permanently delete the bespoke brief from ${brief.full_name}?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-requests/${brief.id}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (r.ok) {
        toast.success('Brief deleted');
        setBriefs(prev => prev.filter(b => b.id !== brief.id));
      } else toast.error('Delete failed');
    } catch { toast.error('Network error'); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl text-white font-light flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-[#D4AF37]" /> Bespoke Briefs Pipeline
        </h2>
        <p className="text-white/50 text-sm mt-1">
          Track every submission from the <code className="text-[#D4AF37] bg-black/30 px-1.5 py-0.5 rounded text-[11px]">/bespoke-sourcing</code> form through the {depositWeeks}-week sourcing window.
        </p>
      </div>

      {/* Pipeline Health widget — money-on-the-table command-centre */}
      {health && (
        <PipelineHealth health={health} active={filter} onPick={setFilter} />
      )}

      {/* Stage filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border transition-colors ${filter === 'all' ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'border-white/15 text-white/60 hover:text-white hover:bg-white/5'}`}
          data-testid="brief-filter-all"
        >
          All <span className="opacity-70 ml-1">({stageCounts.all || 0})</span>
        </button>
        {STAGES.map(s => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className={`px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border transition-colors ${filter === s.id ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : `${s.color.replace('/15', '/8').replace('/30', '/15').replace('/40', '/15')} hover:opacity-80`}`}
            data-testid={`brief-filter-${s.id}`}
          >
            {s.label} <span className="opacity-70 ml-1">({stageCounts[s.id] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, phone, city, country…"
          className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none"
          data-testid="brief-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <p className="text-white/40 text-sm text-center py-12">Loading briefs…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40 text-sm bg-white/[0.02] border border-dashed border-white/10 rounded-lg">
          {briefs.length === 0
            ? 'No bespoke briefs yet. Submissions from /bespoke-sourcing land here automatically.'
            : 'No briefs match the current filter / search.'}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(b => (
            <BriefRow key={b.id} brief={b} onUpdate={onUpdate} onDelete={onDelete} depositWeeks={depositWeeks} />
          ))}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={load} variant="outline" size="sm" className="border-white/15 text-white/60 hover:text-white" data-testid="brief-refresh">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>
    </div>
  );
};
